import { mergeClasses } from "@fluentui/react-components";
import * as React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import useMedia from "../../hooks/useMedia/useMedia";
import { useStyles } from "./Carousel.web.styles";

export type CarouselProps = {
  id: string;
  children: React.ReactNode;
  setSwipedState?: (isSwiped: boolean) => void;
};

const maxNumberOfSlides = 5;

const getParentDivFromId = (id: string) => {
  return document.querySelector(`[id="${id}"]`);
};

const getCurrentSlideElement = (id: string) => {
  return document.querySelector(`#${id} .slick-current`) as HTMLElement;
};

export const Carousel: React.FC<CarouselProps> = ({
  id,
  children,
  setSwipedState,
}) => {
  const styles = useStyles();
  const isOneColumn = useMedia({ maxWidth: "479px" });
  const [activeDot, setActiveDot] = React.useState<number>(0);
  const sliderRef = React.useRef<Slider | null>(null);
  const settings = {
    arrows: false,
    ref: sliderRef,
    dots: true,
    speed: 250,
    swipe: true,
    infinite: false,
    slidesToShow: isOneColumn ? 1 : 2,
    slidesToScroll: isOneColumn ? 1 : 2,
    dotsClass: styles.slickDots,
    // Unfortunately, centerMode is not working properly with 2 slides due to library bug https://github.com/akiran/react-slick/issues/1796
    centerMode: isOneColumn,
    centerPadding: "16px",
    appendDots: (dots: any) => (
      <div>
        <ul className={styles.slickDots}>{dots}</ul>
      </div>
    ),
    customPaging: (i: number) => (
      <button
        className={mergeClasses(
          styles.dot,
          i === activeDot ? styles.active : undefined
        )}
        id={`${id}-slick-dot-${i}`}
        aria-label={(i + 1).toString()}
      ></button>
    ),
    beforeChange: (_: number, nextSlide: number) => {
      const dot = isOneColumn ? nextSlide : Math.ceil(nextSlide / 2);

      setActiveDot(dot);
    },
    afterChange: (_: number) => {
      // After slider change, focus on first item on displayed items
      const currentSlideElement = getCurrentSlideElement(id);
      const firstFocusableElement = currentSlideElement?.querySelector(
        '[tabindex="0"], a'
      ) as HTMLElement;
      firstFocusableElement?.focus();
      setSwipedState?.(false);
    },
    onSwipe: () => {
      setSwipedState?.(true);
    },
  };

  React.useEffect(() => {
    const updateSliderBasedOnItemFocus = () => {
      const focusedElement = document.activeElement as HTMLElement;
      const parentDiv = focusedElement?.closest(".slick-slide");
      // If focus changes to be outside of current displayed items then update the slider
      if (parentDiv && !parentDiv.classList.contains("slick-active")) {
        const idx = parseInt(parentDiv?.getAttribute("data-index") as string);
        // If one column simply update the slider to the index of the item
        // If two columns, update the slider to the index of the first item in the row
        const slide = isOneColumn ? idx : idx % 2 === 0 ? idx : idx - 1;
        // Hack due to library limitations:
        // For two columns, focus must be momentarily set to the first item because item will not be centered correctly.
        if (!isOneColumn) {
          const firstChildDiv = getCurrentSlideElement(id);
          firstChildDiv?.focus();
        }
        sliderRef.current?.slickGoTo(slide, true);
      }
    };
    const parentDiv = getParentDivFromId(id);

    parentDiv?.addEventListener("focusin", updateSliderBasedOnItemFocus);
    return () => {
      parentDiv?.removeEventListener("focusin", updateSliderBasedOnItemFocus);
    };
  }, [isOneColumn]);

  return (
    <div id={id}>
      <Slider className={styles.slider} {...settings}>
        {React.Children.toArray(children).slice(0, maxNumberOfSlides)}
      </Slider>
    </div>
  );
};
